
















































































































































































































































































































// core
import { startCase } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

// models
import {
  Employee,
  EmployeeFieldError,
  EmployeeHourlySubType,
  EmployeeSalarySubType,
  EmployeeType,
  IEmployee,
  IUser
} from '@/models';

//components
import EmployeePicker from './EmployeePicker.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import DragDropFileInput from '@/components/common/DragDropFileInput.vue';
import EmployeeUserPicker from './EmployeeUserPicker.vue';
import CountryPicker from '../common/CountryPicker.vue';
import HRDepartmentPicker from './hr/HRDepartmentPicker.vue';
import EmployeeWorkgroupPicker from './EmployeeWorkgroupPicker.vue';
import PhoneInputPicker from '@/components/core/PhoneInputPicker/Index.vue';

// utils
import mixins from '@/utils/mixins';
import { diffDeep } from '@/utils/helpers';

//mixins
import FormRules from '@/mixins/file-upload-rules';
import { factory as Proxyable } from '@/mixins/proxyable';
const baseMixins = mixins(FormRules, Proxyable<'value', IEmployee>());

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    files: HTMLInputElement;
    employeePicker: InstanceType<typeof EmployeePicker>;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'EmployeeDetailCard',

    components: {
      ErrorAlert,
      EmployeePicker,
      DragDropFileInput,
      EmployeeUserPicker,
      CountryPicker,
      HRDepartmentPicker,
      EmployeeWorkgroupPicker,
      PhoneInputPicker
    },

    props: {
      isEdit: {
        type: Boolean,
        default: false
      },

      fieldError: {
        type: Object,
        default: () => new EmployeeFieldError()
      }
    },

    data() {
      return {
        requiredRules: [(v: string) => !!v || 'This field is required'],

        employeeTypes: [
          {
            display_name: 'Hourly',
            value: EmployeeType.Hourly
          },
          {
            display_name: 'Salary',
            value: EmployeeType.Salary
          }
        ],

        EmployeeType,

        employeeSubTypeSalaryList: [
          {
            value: EmployeeSalarySubType.Salary,
            display_name: startCase(
              EmployeeSalarySubType[EmployeeSalarySubType.Salary]
            )
          },
          {
            value: EmployeeSalarySubType.SalaryContract,
            display_name: startCase(
              EmployeeSalarySubType[EmployeeSalarySubType.SalaryContract]
            )
          },
          {
            value: EmployeeSalarySubType.TempAgency,
            display_name: startCase(
              EmployeeSalarySubType[EmployeeSalarySubType.TempAgency]
            )
          },
          {
            value: EmployeeSalarySubType.CoOp,
            display_name: startCase(
              EmployeeSalarySubType[EmployeeSalarySubType.CoOp]
            )
          },
          {
            value: EmployeeSalarySubType.PartTime,
            display_name: startCase(
              EmployeeSalarySubType[EmployeeSalarySubType.PartTime]
            )
          }
        ],

        employeeSubTypeHourlyList: [
          {
            value: EmployeeHourlySubType.DayShift,
            display_name: startCase(
              EmployeeHourlySubType[EmployeeHourlySubType.DayShift]
            )
          },
          {
            value: EmployeeHourlySubType.AfternoonShift,
            display_name: startCase(
              EmployeeHourlySubType[EmployeeHourlySubType.AfternoonShift]
            )
          }
        ]
      };
    },

    computed: {
      ...mapGetters({
        getEmployeeById: 'employee/getEmployeeById'
      }),

      employeeIsSalary(): boolean {
        return this.internalValue.type == EmployeeType.Salary;
      },

      employeeIsHourly(): boolean {
        return this.internalValue.type == EmployeeType.Hourly;
      },

      employeeSubTypes(): { value: number; display_name: string }[] {
        switch (this.internalValue.type) {
          case EmployeeType.Salary:
            return this.employeeSubTypeSalaryList;
          case EmployeeType.Hourly:
          default:
            return this.employeeSubTypeHourlyList;
        }
      }
    },

    methods: {
      ...mapActions({
        createEmployee: 'employee/createEmployee',
        updateEmployee: 'employee/updateEmployee'
      }),

      onPhoneNumberInput(
        _formattedNumber: string,
        { number }: { number: { international: string } }
      ) {
        this.internalValue.phone = number.international;
        this.clearAllErrors();
      },

      clearAllErrors(): void {
        this.$emit('clearAllErrors');
      },

      employeeNumberInputHandler(value?: string) {
        if (value) {
          this.internalValue.employee_number = value;
        } else {
          this.internalValue.employee_number = null;
        }
        this.clearAllErrors();
      },

      setEmployeeFromUser(user: IUser) {
        this.internalValue = new Employee({
          ...this.internalValue,
          user: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          is_active: user.is_active
        });
        this.clearAllErrors();
      },

      async saveEmployee(): Promise<number | void> {
        this.clearAllErrors();

        if (
          this.internalValue.supervisor &&
          (this.internalValue.supervisor as IEmployee).id
        ) {
          this.internalValue.supervisor = (this.internalValue
            .supervisor as IEmployee).id;
        }

        if (this.isEdit) {
          const employeeDelta = diffDeep(
            this.internalValue,
            this.getEmployeeById(this.internalValue.id),
            true
          );

          await this.updateEmployee(employeeDelta);
        } else {
          return await this.createEmployee(this.internalValue);
        }
      }
    },

    async mounted() {
      // wait for employee picker to mount
      this.$nextTick(() => {
        // fetch twice in case of current supervisor not being in inital loaded list
        this.$refs.employeePicker.debouncefetchEmployees();
      });
    }
  });
